import { useMemo } from "react";

import { PalTextField, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";

import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";

import PalamarLogo from "~assets/images/logo-filled-notext.svg";
import LegoLottie from "~assets/lottie/lego.json";
import PRProfile, { PRProfileLottie } from "~components/Generic/PRProfile";
import { popupIconType, popupIconTypeOptions, popupPositionOptions } from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import FileHelper from "~helpers/FileHelper";
import Utils from "~helpers/Utils";

import { IFRAME_PREVIEW_ID, StepContainer } from "../CommonComponents";

function StepChatbot({ formik, setFieldValue, stepKey }) {
  const { t } = useTranslation();

  const { popupPalActions, popupInternal } = useMemo(() => {
    const iframeDom = document.getElementById(IFRAME_PREVIEW_ID);
    const palActions = iframeDom?.contentWindow?.palActions;
    const internalDoc = iframeDom?.contentWindow?.document;

    const innerIframe = internalDoc?.getElementById("_palamar_chat_iframe");
    const innerWindow = innerIframe?.contentWindow;
    const popup = innerWindow?.popup;
    if (popup) {
      popup.setPreviewState({ enableInput: false });
    }
    return { popupPalActions: palActions, popupInternal: popup };
  }, []);

  const handleFileChange = (key, isLottie) => async (file, e) => {
    if (!isLottie) {
      const img = await FileHelper.getImage(file);
      const width = img.width;
      const height = img.height;
      if (width !== height || width < 144) {
        AlertHelper.showError(t("dashboard.popup.errors.headerIconSize"));
        e.preventDefault();
        return;
      }
    } else {
      const lottieObj = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = function (e) {
          let json;
          try {
            json = JSON.parse(e.target.result);
          } catch {}
          resolve(json);
        };
        reader.readAsText(file);
      });
      if (!lottieObj?.v) {
        AlertHelper.showError(t("dashboard.popup.errors.invalidLottie"));
        return;
      }
    }

    // setFiles({ ...files, popup_button_icon_path: [file] });
    // if (popupSettings?.popup_style_info?.popup_icon_type === popupIconType.lottie) {
    //   dispatch(setPopupSettingsState("popup_button_icon_path", file));
    // } else {
    //   const blobUri = URL.createObjectURL(file);
    //   dispatch(setPopupSettingsState("popup_button_icon_path", blobUri));
    // }
    await formik.setFieldValue(`${key}___file`, file);
    const blobUri = URL.createObjectURL(file);
    await formik.setFieldValue(key, blobUri);
  };

  const handleChangeRadio = (key) => async (e) => {
    const value = e.target.value;
    await formik.setFieldValue(key, value);
    if (key === "welcomeForm.popup_style_info.popup_position") {
      popupPalActions?.setMinimize(true);
    } else if (key === "welcomeForm.popup_style_info.popup_icon_type") {
      popupPalActions?.setMinimize(true);
      if (value === popupIconType.icon) {
        const fileItem = await Utils.urlToFile(PalamarLogo, "defaultHeaderIcon");
        await formik.setFieldValue("files", { ...formik.values.files, popup_button_icon_path: [fileItem] });
        await formik.setFieldValue("welcomeForm.popup_button_icon_path", URL.createObjectURL(fileItem));
      } else if (value === popupIconType.lottie) {
        const file = new File([JSON.stringify(LegoLottie)], "lottie.json", { type: "text/plain" });
        await formik.setFieldValue("files", { ...formik.values.files, popup_button_icon_path: [file] });
        const blobUrl = URL.createObjectURL(file);
        await formik.setFieldValue("welcomeForm.popup_button_icon_path", blobUrl);
      }
    } else if (key === "welcomeForm.popup_style_info.popup_header_icon_type") {
      popupPalActions?.setMinimize(false);
      if (value === popupIconType.icon) {
        const fileItem = await Utils.urlToFile(PalamarLogo, "defaultHeaderIcon");
        await formik.setFieldValue("files", { ...formik.values.files, popup_header_icon_path: [fileItem] });
        await formik.setFieldValue("welcomeForm.popup_header_icon_path", URL.createObjectURL(fileItem));
      } else if (value === popupIconType.lottie) {
        const file = new File([JSON.stringify(LegoLottie)], "lottie.json", { type: "text/plain" });
        await formik.setFieldValue("files", { ...formik.values.files, popup_header_icon_path: [file] });
        const blobUrl = URL.createObjectURL(file);
        await formik.setFieldValue("welcomeForm.popup_header_icon_path", blobUrl);
      }
    } else if (key === "welcomeForm.popup_style_info.popup_bot_icon_type") {
      popupPalActions?.setMinimize(false);
      if (value === popupIconType.icon) {
        const fileItem = await Utils.urlToFile(PalamarLogo, "defaultHeaderIcon");
        await formik.setFieldValue("files", { ...formik.values.files, popup_bot_icon_path: [fileItem] });
        await formik.setFieldValue("welcomeForm.popup_bot_icon_path", URL.createObjectURL(fileItem));
      } else if (value === popupIconType.lottie) {
        const file = new File([JSON.stringify(LegoLottie)], "lottie.json", { type: "text/plain" });
        await formik.setFieldValue("files", { ...formik.values.files, popup_bot_icon_path: [file] });
        const blobUrl = URL.createObjectURL(file);
        await formik.setFieldValue("welcomeForm.popup_bot_icon_path", blobUrl);
      }
    }
  };

  const handleChangeTextField = (key) => (e) => {
    formik.setFieldValue(key, e.target.value);
    popupPalActions?.setMinimize(true);
    if (key === "welcomeForm.welcome_msg") {
      // popupInternal.IFrameHelper.sendActionToBalloon({
      //   type: "visibility",
      //   payload: true,
      // });
      // popupInternal.IFrameHelper.sendActionToBalloon({
      //   type: "messages",
      //   payload: [
      //     {
      //       text: e.target.value,
      //     },
      //   ],
      // });
    }
  };
  const handleChangeHeaderText = (key) => (e) => {
    formik.setFieldValue(key, e.target.value);
    popupPalActions?.setMinimize(false);
  };

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            Popup Styling
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please provide the styling details for the popup, including colors, position, and any other relevant
            information.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Header Text
          </PalTypography>
          <PalTextField
            fullWidth
            placeholder="Enter Popup Header Text"
            value={formik.values.welcomeForm.popup_title}
            onChange={handleChangeHeaderText("welcomeForm.popup_title")}
          />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Position
          </PalTypography>
          <RadioGroup
            row
            aria-labelledby="popup-position"
            name="welcomeForm.popup_style_info?.popup_position"
            value={formik.values.welcomeForm.popup_style_info?.popup_position}
            onChange={handleChangeRadio("welcomeForm.popup_style_info.popup_position")}
          >
            {[...popupPositionOptions].reverse().map((item) => (
              <FormControlLabel key={item.value} control={<Radio />} label={t(item.label)} value={item.value} />
            ))}
          </RadioGroup>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Popup Icon Type
          </PalTypography>

          <RadioGroup
            row
            aria-labelledby="popup-header-icon-type"
            name="welcomeForm.popup_style_info?.popup_icon_type"
            value={formik.values.welcomeForm.popup_style_info?.popup_icon_type}
            onChange={handleChangeRadio("welcomeForm.popup_style_info.popup_icon_type")}
          >
            {popupIconTypeOptions
              .filter((item) => ![popupIconType.none].includes(item.value))
              .map((item) => (
                <FormControlLabel key={item.value} control={<Radio />} label={t(item.label)} value={item.value} />
              ))}
          </RadioGroup>
        </Grid>
        {formik.values.welcomeForm?.popup_style_info?.popup_icon_type !== popupIconType.default && (
          <Grid item xs={12}>
            <PalTypography gutterBottom fontWeight={600} variant="body1">
              Popup Icon
            </PalTypography>
            {formik.values.welcomeForm?.popup_style_info?.popup_icon_type === popupIconType.icon && (
              <PRProfile
                img={formik.values.welcomeForm.popup_button_icon_path}
                onFileChange={handleFileChange("welcomeForm.popup_button_icon_path")}
              />
            )}
            {formik.values.welcomeForm?.popup_style_info?.popup_icon_type === popupIconType.lottie && (
              <PRProfileLottie
                img={formik.values.welcomeForm.popup_button_icon_path}
                onFileChange={handleFileChange("welcomeForm.popup_button_icon_path", true)}
              />
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Header Icon Type
          </PalTypography>

          <RadioGroup
            row
            aria-labelledby="popup-header-icon-type"
            name="welcomeForm.popup_style_info?.popup_header_icon_type"
            value={formik.values.welcomeForm.popup_style_info?.popup_header_icon_type}
            onChange={handleChangeRadio("welcomeForm.popup_style_info.popup_header_icon_type")}
          >
            {popupIconTypeOptions
              .filter((item) => ![popupIconType.lottie].includes(item.value))
              .map((item) => (
                <FormControlLabel key={item.value} control={<Radio />} label={t(item.label)} value={item.value} />
              ))}
          </RadioGroup>
        </Grid>
        {formik.values.welcomeForm?.popup_style_info?.popup_header_icon_type === popupIconType.icon && (
          <Grid item xs={12}>
            <PalTypography gutterBottom fontWeight={600} variant="body1">
              Header Icon
            </PalTypography>
            {formik.values.welcomeForm?.popup_style_info?.popup_header_icon_type === popupIconType.icon && (
              <PRProfile
                img={formik.values.welcomeForm.popup_header_icon_path}
                onFileChange={handleFileChange("welcomeForm.popup_header_icon_path")}
              />
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Bot Joined Icon Type
          </PalTypography>

          <RadioGroup
            row
            aria-labelledby="popup-header-icon-type"
            name="welcomeForm.popup_style_info?.popup_bot_icon_type"
            value={formik.values.welcomeForm.popup_style_info?.popup_bot_icon_type}
            onChange={handleChangeRadio("welcomeForm.popup_style_info.popup_bot_icon_type")}
          >
            {popupIconTypeOptions
              .filter((item) => ![popupIconType.none, popupIconType.lottie].includes(item.value))
              .map((item) => (
                <FormControlLabel key={item.value} control={<Radio />} label={t(item.label)} value={item.value} />
              ))}
          </RadioGroup>
        </Grid>
        {formik.values.welcomeForm?.popup_style_info?.popup_bot_icon_type !== popupIconType.default && (
          <Grid item xs={12}>
            <PalTypography gutterBottom fontWeight={600} variant="body1">
              Bot Joined
            </PalTypography>
            {formik.values.welcomeForm?.popup_style_info?.popup_bot_icon_type === popupIconType.icon && (
              <PRProfile
                img={formik.values.welcomeForm.popup_bot_icon_path}
                onFileChange={handleFileChange("welcomeForm.popup_bot_icon_path")}
              />
            )}
            {formik.values.welcomeForm?.popup_style_info?.popup_bot_icon_type === popupIconType.lottie && (
              <PRProfileLottie
                img={formik.values.welcomeForm.popup_bot_icon_path}
                onFileChange={handleFileChange("welcomeForm.popup_bot_icon_path", true)}
              />
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Balloon Message
          </PalTypography>
          <PalTextField
            fullWidth
            placeholder="Enter Balloon Message"
            value={formik.values.welcomeForm.welcome_msg}
            onChange={handleChangeTextField("welcomeForm.welcome_msg")}
          />
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepChatbot;
